import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './Equity.css';
import './MarginV2.css';

/* ------------------------------------------------------------------ *
 * Margin page v2. All numbers, zones, copy and the cushion chart (an
 * inline SVG) are computed server-side by portfolio/services/margin_v2.py
 * and served by GET /portfolio/margin/v2/ - this component only lays them
 * out. Shown instead of Margin.js by MarginSwitch.js.
 * ------------------------------------------------------------------ */

const FONT_LINK_ID = 'mv2-font';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Instrument+Sans:wdth,wght@75..100,400..700&display=swap';

/** Instrument Sans is only used inside .mv2, so it's linked from here
 * rather than from index.html - v1 and the rest of the app never load it. */
function useInstrumentSans() {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

const CHIP_CLASS = { comfortable: 'mv2-chip', watch: 'mv2-chip mv2-chip--watch', tight: 'mv2-chip mv2-chip--tight' };

function ChipIcon({ zone }) {
  const common = {
    width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 3, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
  };
  if (zone === 'comfortable') return <svg {...common}><path d="M5 12l5 5 9-10" /></svg>;
  if (zone === 'watch') return <svg {...common}><path d="M12 6v7" /><path d="M12 18h.01" /></svg>;
  return <svg {...common}><path d="M12 3L2 21h20L12 3z" /><path d="M12 10v4" /><path d="M12 18h.01" /></svg>;
}

function StaleBand({ account, onRefresh, refreshing }) {
  return (
    <div className="mv2-band" role="status">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <p>
        <strong>
          {account.stale_since_text
            ? `These figures are from ${account.stale_since_text}, ${account.stale_age_text}.`
            : 'These figures have never been updated.'}
        </strong>{' '}
        TWS isn't connected, so nothing on this page has updated since. Treat the cushion as out of date until you reconnect.
      </p>
      <button type="button" className="mv2-btn mv2-btn--ghost" onClick={onRefresh} disabled={refreshing}>
        {refreshing ? 'Refreshing…' : 'Refresh now'}
      </button>
    </div>
  );
}

function Meter({ meter }) {
  const last = meter.axis.length - 1;
  return (
    <div className="mv2-meter">
      <div className="mv2-meter__head">
        <span className="mv2-meter__label"><abbr title={meter.abbr_title}>{meter.label}</abbr></span>
        <span className="mv2-meter__value">{meter.value_text}</span>
      </div>
      <div className="mv2-meter__track">
        <span className="mv2-z1" style={{ width: `${meter.zone_widths[0]}%` }} />
        <span className="mv2-z2" style={{ width: `${meter.zone_widths[1]}%` }} />
        <span className="mv2-z3" style={{ width: `${meter.zone_widths[2]}%` }} />
        <span className="mv2-meter__marker" style={{ '--at': `${meter.marker_pct}%` }} aria-hidden="true" />
      </div>
      <div className="mv2-meter__axis">
        {meter.axis.map((tick, i) => (
          <span key={tick.text} style={i === 0 || i === last ? undefined : { left: `${tick.at}%` }}>{tick.text}</span>
        ))}
      </div>
      <p className="mv2-note">{meter.note}</p>
    </div>
  );
}

function FigureRow({ row, isLast }) {
  if (row.dim) {
    return (
      <div className="mv2-row mv2-row--dim">
        <span>{row.label}{row.hint ? <small>{row.hint}</small> : null}</span>
        <span>{row.value}</span>
      </div>
    );
  }
  let labelClass;
  if (row.key_color) labelClass = 'mv2-key';
  else if (row.indent) labelClass = 'mv2-indent';
  return (
    <div className={`mv2-row${isLast ? ' mv2-row--last' : ''}`}>
      <span className={labelClass} style={row.key_color ? { '--k': row.key_color } : undefined}>{row.label}</span>
      <span className={`mv2-row__v${row.strong ? ' mv2-row__v--strong' : ''}`}>{row.value}</span>
    </div>
  );
}

function AccountView({ account, onRefresh, refreshing, flash }) {
  const id = account.account_id;
  const titlebar = (
    <div className="mv2-titlebar">
      <h1>Margin</h1>
      <span className="mv2-muted">IBKR account {id}</span>
      <span className="mv2-updated">{account.updated_text}</span>
    </div>
  );
  const flashLine = flash ? (
    <p className={`mv2-flash${flash.ok ? '' : ' mv2-flash--error'}`} role="status">{flash.message}</p>
  ) : null;

  return (
    <div className={`mv2${account.is_stale ? ' mv2--stale' : ''}`}>
      {account.is_stale && <StaleBand account={account} onRefresh={onRefresh} refreshing={refreshing} />}
      <div className="mv2-main">
        {titlebar}
        {flashLine}

        {account.empty ? (
          <p className="mv2-empty">
            The latest snapshot for this account has no positive net liquidation, so there is no
            cushion or margin usage to show.
          </p>
        ) : (
          <>
            <section className="mv2-hero" aria-labelledby={`mv2-room-${id}`}>
              <div className="mv2-hero__main">
                <p id={`mv2-room-${id}`} className="mv2-hero__num">{account.hero.amount_text}</p>
                <p className="mv2-hero__lead">can be lost before your account hits the margin call line.</p>
              </div>
              <div className="mv2-hero__side">
                <div className="mv2-hero__pct">
                  <span>{account.hero.pct_text}</span>
                  <span className={CHIP_CLASS[account.hero.zone]}>
                    <ChipIcon zone={account.hero.zone} />{account.hero.zone_label}
                  </span>
                </div>
                <p className="mv2-note">{account.hero.note}</p>
              </div>
            </section>

            <section
              className="mv2-scale"
              aria-label="Distance to margin call"
              style={{ '--maint': `${account.scale.maint_pct}%`, '--initial': `${account.scale.initial_pct}%` }}
            >
              <div className="mv2-scale__top">
                <span className="mv2-muted" style={{ left: 0 }}>{account.scale.zero_text}</span>
                <span className="mv2-scale__call">{account.scale.call_text}</span>
                <span className="mv2-scale__end">{account.scale.net_liq_text}</span>
              </div>
              <div className="mv2-scale__bar">
                <div className="mv2-scale__fill">
                  {account.scale.cushion_text}<span className="mv2-fill-extra">&nbsp;(excess liquidity)</span>
                </div>
              </div>
              <span className="mv2-scale__line" aria-hidden="true" />
              <span className="mv2-scale__tick" aria-hidden="true" />
              <div className="mv2-scale__bottom">
                <span style={{ left: 0 }}>Held as maintenance margin</span>
                <span className="mv2-scale__initial">{account.scale.initial_text}</span>
              </div>
            </section>

            <div className="mv2-cols">
              <section className="mv2-usage" aria-labelledby={`mv2-usage-h-${id}`}>
                <h2 id={`mv2-usage-h-${id}`}>How hard the account is working</h2>
                {account.meters.map((meter) => <Meter key={meter.key} meter={meter} />)}
                <p className="mv2-profile">{account.profile_text}</p>
              </section>

              <section className="mv2-figures" aria-labelledby={`mv2-fig-h-${id}`}>
                <h2 id={`mv2-fig-h-${id}`}>Account figures</h2>
                {account.figures.map((group) => (
                  <React.Fragment key={group.heading}>
                    <h3>{group.heading}</h3>
                    {group.rows.map((row, i) => (
                      <FigureRow key={row.label} row={row} isLast={i === group.rows.length - 1} />
                    ))}
                  </React.Fragment>
                ))}
              </section>
            </div>

            <section className="mv2-trend" aria-labelledby={`mv2-trend-h-${id}`}>
              <div className="mv2-trend__head">
                <h2 id={`mv2-trend-h-${id}`}>Cushion over the last two weeks</h2>
                {account.trend.legend.map((item) => (
                  <span key={item.text} className="mv2-legend" style={{ '--k': item.color }}>{item.text}</span>
                ))}
              </div>
              {/* Server-generated SVG (portfolio/services/margin_v2.py); all text in it is escaped there. */}
              <div className="mv2-trend__scroll" dangerouslySetInnerHTML={{ __html: account.trend.svg }} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function MarginV2() {
  useInstrumentSans();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [flash, setFlash] = useState(null); // { ok: bool, message: string }

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_URL}/portfolio/margin/v2/`);
      setAccounts(response.data.accounts || []);
    } catch (err) {
      console.error('Error fetching margin v2 data:', err);
      setError('Could not load margin data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 6000);
    return () => clearTimeout(t);
  }, [flash]);

  // Same refresh action as v1's "Refresh Now" button (Margin.js).
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API_URL}/portfolio/margin/refresh/`);
      setFlash({ ok: response.data.ok, message: response.data.message });
      if (response.data.ok) await fetchData();
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Refresh failed - unknown error.';
      setFlash({ ok: false, message });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading || error || accounts.length === 0) {
    return (
      <div className="equity-container">
        <div className="mv2">
          <div className="mv2-main">
            <div className="mv2-titlebar"><h1>Margin</h1></div>
            <p className="mv2-empty" role={error ? 'alert' : undefined}>
              {loading && 'Loading…'}
              {error}
              {!loading && !error && (
                'No live account summary yet. It is written hourly by '
                + 'scripts/ibkr/snapshot_account_summary.py while a TWS connection is available.'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="equity-container">
      {accounts.map((account) => (
        <AccountView
          key={account.account_id}
          account={account}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          flash={flash}
        />
      ))}
    </div>
  );
}

export default MarginV2;
